/**
 * 一括操作の結果トーストの規則 (純関数)。 外部へまとめて送った札の成立・失敗確定を、 一覧の
 * 観測から件数でまとめて知らせるための「追跡の状態」と「1 回の観測の差分」を持つ。
 * 時間前の一括板取消 (AC-BIN-112) とスポットの一括取下 (AC-BSP-116) が同じ規則を使う。
 *
 * 市場で違うもの (札の結果の読み方 = classify、 key の取り方、 文言、 出し方の方針) は caller が
 * 持つ。 本 module は市場に依存する import を持たない。
 *
 * 共通の規則:
 * - 一括 (batch) ごとに受付時刻と「まだ結果が出ていない key」 を持つ
 * - 受付時刻以前に取得した一覧の観測は数えない (受付前の値で、 開き直した取消失敗の札を
 *   新しい失敗と読まないため)
 * - 各札は batch の中で 1 回だけ数える
 * - batch を受付後に最初に観測した時に処理中の札があれば、 それを返す (処理中の知らせは 1 回だけ)
 * - 'unknown' と一覧に無い key は数えず、 追い続ける
 * - 全件が結果を出した batch は外す
 * - 同じ key を新しい一括で追い直すと、 古い batch から外して新しい batch でもう一度数える
 *
 * 出し方の方針 (`BulkEmitPolicy`):
 * - perObservation: 観測ごとに、 新しく成立した札・新しく失敗が確定した札を返す
 * - oncePerBatch: 一括ごとに成功 1 通・失敗 1 通。 成立は受付後の最初の観測で成立していた札だけを
 *   返し、 後から成立した札は返さない。 失敗は batch の札がすべて結果を出した観測で、 それまでに
 *   失敗が確定した札をまとめて返す
 */

/** 札 1 件の、 その観測での結果。 R は失敗の理由 (caller が決める)。 */
export type BulkOutcome<R extends string> =
  | 'pending'
  | 'succeeded'
  | 'unknown'
  | { failed: R }

/**
 * 結果の出し方の方針。 時間前 (AC-BIN-112) は oncePerBatch、 スポット (AC-BSP-116) は
 * perObservation。
 */
export type BulkEmitPolicy = 'oncePerBatch' | 'perObservation'

/** 失敗が確定した札 (key は追跡から外す時に引く)。 */
type HeldFailure<T, R extends string> = { key: string; item: T; reason: R }

/** 追跡の状態。 batch は受け付けた順。 */
export type BulkTracking<T = unknown, R extends string = string> = {
  readonly batches: ReadonlyArray<{
    acceptedAtMs: number
    /** まだ結果が出ていない key。 */
    unsettled: ReadonlySet<string>
    /**
     * batch の札が載った受付後の観測を当てたか (処理中と、 oncePerBatch の成功は最初の観測だけで
     * 数える)。 batch の札が 1 件も無い観測 (別の受渡日の一覧) は当てたことにしない。
     */
    observed: boolean
    /** oncePerBatch で、 batch が終わるまで返さずに持っている失敗 (perObservation は常に空)。 */
    failed: ReadonlyArray<HeldFailure<T, R>>
  }>
}

/** 1 回の観測の差分 (この観測で知らせる札)。 */
export type BulkObservation<T, R extends string> = {
  /** 成立した札 (oncePerBatch は受付後の最初の観測で成立していた札だけ)。 */
  succeeded: readonly T[]
  /** この観測が最初になる batch の、 処理中の札。 */
  pendingAtFirst: readonly T[]
  /** 失敗が確定した札 (oncePerBatch は、 この観測で全件が結果を出した batch の失敗)。 */
  failed: ReadonlyArray<{ item: T; reason: R }>
  next: BulkTracking<T, R>
}

/** 何も追っていない状態。 */
export const EMPTY_BULK_TRACKING: BulkTracking<never, never> = { batches: [] }

/**
 * key を全 batch から外す。 まだ結果が出ていない札も持っている失敗も無くなった batch は除く
 * (失敗だけが残った batch は、 次の観測でその失敗を返してから外す)。
 */
const withoutKeys = <T, R extends string>(
  t: BulkTracking<T, R>,
  keys: ReadonlySet<string>,
): BulkTracking<T, R>['batches'] =>
  t.batches
    .map((batch) => ({
      ...batch,
      unsettled: new Set([...batch.unsettled].filter((k) => !keys.has(k))),
      failed: batch.failed.filter((f) => !keys.has(f.key)),
    }))
    .filter((batch) => batch.unsettled.size > 0 || batch.failed.length > 0)

/**
 * 受け付けた key を新しい batch で追い始める。 渡した key は古い batch から外す
 * (前の一括で知らせた札を次の一括で取り消し直すと、 もう一度数える)。 key が無ければ
 * batch を足さない。
 */
export function trackBulk<T, R extends string>(
  t: BulkTracking<T, R>,
  keys: readonly string[],
  acceptedAtMs: number,
): BulkTracking<T, R> {
  const keySet = new Set(keys)
  const kept = withoutKeys(t, keySet)
  if (keySet.size === 0) return { batches: kept }
  return {
    batches: [
      ...kept,
      { acceptedAtMs, unsettled: keySet, observed: false, failed: [] },
    ],
  }
}

/** key の追跡をやめる (1 件の操作で札ごとの知らせに戻す時)。 */
export function releaseBulk<T, R extends string>(
  t: BulkTracking<T, R>,
  keys: readonly string[],
): BulkTracking<T, R> {
  return { batches: withoutKeys(t, new Set(keys)) }
}

/** key の結果がまだ出ていないまま追っているか。 */
export function isBulkTracked(
  t: BulkTracking<unknown, string>,
  key: string,
): boolean {
  return t.batches.some((batch) => batch.unsettled.has(key))
}

/**
 * 一覧の 1 回の観測 (`observedAtMs` = その一覧を取得した時刻) を追跡に当てる。
 * `observedAtMs <= acceptedAtMs` の batch は観測しない。
 */
export function observeBulk<T, R extends string>(
  t: BulkTracking<T, R>,
  items: readonly T[],
  keyOf: (item: T) => string,
  classify: (item: T) => BulkOutcome<R>,
  observedAtMs: number,
  emit: BulkEmitPolicy = 'perObservation',
): BulkObservation<T, R> {
  const batches = t.batches.map((batch) => ({
    acceptedAtMs: batch.acceptedAtMs,
    unsettled: new Set(batch.unsettled),
    failed: [...batch.failed],
    observedBefore: batch.observed,
    counts: observedAtMs > batch.acceptedAtMs,
    seen: false,
  }))
  const succeeded: T[] = []
  const pendingAtFirst: T[] = []
  const failed: Array<{ item: T; reason: R }> = []

  for (const item of items) {
    const key = keyOf(item)
    const batch = batches.find((b) => b.unsettled.has(key))
    if (batch === undefined || !batch.counts) continue
    batch.seen = true
    const outcome = classify(item)
    if (outcome === 'succeeded') {
      batch.unsettled.delete(key)
      if (emit === 'perObservation' || !batch.observedBefore) {
        succeeded.push(item)
      }
    } else if (outcome === 'pending') {
      if (!batch.observedBefore) pendingAtFirst.push(item)
    } else if (outcome !== 'unknown') {
      batch.unsettled.delete(key)
      if (emit === 'perObservation') {
        failed.push({ item, reason: outcome.failed })
      } else {
        batch.failed.push({ key, item, reason: outcome.failed })
      }
    }
  }

  // oncePerBatch: 全件が結果を出した batch の失敗を、 ここでまとめて返す。
  for (const batch of batches) {
    if (batch.unsettled.size > 0) continue
    for (const { item, reason } of batch.failed) failed.push({ item, reason })
  }

  return {
    succeeded,
    pendingAtFirst,
    failed,
    next: {
      batches: batches
        .filter((b) => b.unsettled.size > 0)
        .map((b) => ({
          acceptedAtMs: b.acceptedAtMs,
          unsettled: b.unsettled,
          observed: b.observedBefore || b.seen,
          failed: b.failed,
        })),
    },
  }
}
